import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import { getPublicBusinessLogoUrl } from "../../config/api";
import { listingImageUrl } from "../../services/businessListingApi";
import {
  getMarketplaceCategories,
  getMarketplaceListings,
  type MarketplaceCategory,
  type MarketplaceListing,
} from "../../services/marketplaceApi";
import { C, SERIF } from "./MarketplaceShell";
import { SiteFooter, SiteHeader } from "./SiteChrome";
import { SITE_ROOT, SITE_WRAP, useSiteStyles } from "./siteStyles";
import { subscriptionTotalMinor, usePlans } from "../../hooks/usePlanPricing";

const BRAND = "#2C7A5B";

/**
 * The promoted-placement accent.
 *
 * A warm bronze rather than a shade of the brand green: the whole point of a paid spot is that it
 * reads as different at a glance, and a lighter/darker green would read as "the same thing, bigger".
 * It also has to survive sitting next to a business's OWN brand colour on the card panel, which
 * green frequently would not.
 */
const FEATURED = "#A9691B";

// Scoped styles: hover states (React inline styles can't express :hover) plus
// responsive collapse for the fixed desktop grids in the source design.
export const STYLES = `
.pn-root{font-family:'Public Sans',system-ui,sans-serif;background:#FFFFFF;color:#243139;min-height:100%;}
.pn-root ::selection{background:#2C7A5B;color:#fff;}
.pn-navlink:hover{color:#2C7A5B;}
.pn-login:hover{background:#F2F0EB;}
.pn-cta:hover{background:#1F5C44;}
.pn-field:hover{background:#F6F8FC;}
.pn-searchbtn:hover{background:#1F5C44;}
.pn-chip:hover{border-color:#2C7A5B;color:#2C7A5B;}
/* Supplier-profile layout (mockup 1c): content beside a contact rail that follows the reader
   down a long page. Collapses to one column below 900px, where a 340px rail would leave the
   items grid too narrow to show a picture and a price side by side. */
/* Supplier-profile visual direction (mockup 1c). Deliberately scoped to .pn-profile rather
   than .pn-root: browse and results have not been redesigned to match, and quietly restyling
   them from here would be a change nobody asked for. */
.pn-profile{font-family:'Figtree',system-ui,sans-serif;background:#FAF9F5;color:#1C211C;}
.pn-profile .pn-serif{font-family:'Source Serif 4',Georgia,serif;font-weight:500;letter-spacing:-0.01em;}
.pn-profile .pn-navbar{background:#FDFCF9;border-bottom:1px solid #E7E5DE;}
/* Two columns, and deliberately only TWO grid items: everything on the left lives inside
   .pn-profile-main. An earlier version made the head, the body and the rail three grid items and
   spanned the rail across both rows — but a grid item spanning two auto-sized rows has its height
   distributed between them, so the tall rail inflated the row holding the name and left a hole
   under it. With no spanning there is nothing to distribute, and the rail still starts level with
   the heading because both columns start at the top. */
.pn-profile-grid{display:grid;grid-template-columns:1fr 340px;column-gap:32px;align-items:start;}
.pn-profile-main{min-width:0;}
.pn-profile-body{min-width:0;}
.pn-profile-rail{position:sticky;top:96px;}
@media (max-width:900px){
  /* One column, and the order must be name, then form, then the rest. display:contents dissolves
     the left wrapper so its two children become flex items of the grid alongside the rail, which
     is what lets the form sit BETWEEN them. Ordering the rail first outright would put an enquiry
     form above the name of the business it enquires with; leaving DOM order would bury it beneath
     the products grid. */
  .pn-profile-grid{display:flex;flex-direction:column;}
  .pn-profile-main{display:contents;}
  .pn-profile-head{order:1;}
  .pn-profile-rail{order:2;position:static;margin-bottom:24px;}
  .pn-profile-body{order:3;}
}
/* The profile's hero keeps a SHAPE, not a height: 3:1 inside the page column on a desktop, so a
   cover is cropped the same way at every window width (a fixed 280px band across 1920px was ~7:1
   and cut a 2:1 cover's top and bottom away). Covers are recommended at 3:1 (1600 x 533). */
.pn-profile-hero{aspect-ratio:3/1;}
/* The profile's masthead on a phone (mobile-screen-08).
   The desktop proportions — a 112px logo tile hanging 56px below the hero, and 72px of clearance
   under all that — were drawn for a 1100px column. At 390px they push the name below the fold, so
   the masthead shrinks: the hero goes full-bleed at 2:1 (195px tall at 390px, so a cover is barely
   cropped), a 72px mark, and 20px gutters.
   Values are !important where the desktop one is an INLINE style on the element (this page is
   styled inline throughout, not with utility classes), and an author rule only outranks an inline
   declaration when it is important. */
@media (max-width:560px){
  .pn-profile-hero-wrap{padding:0!important;}
  .pn-profile-hero{aspect-ratio:2/1;border-radius:0!important;}
  .pn-profile-mark{width:72px!important;height:72px!important;bottom:-36px!important;left:20px!important;border-radius:16px!important;}
  .pn-profile-mark .pn-serif{font-size:26px!important;}
  /* 56px of top padding clears the 36px the mark hangs by and leaves a 20px gap under it. */
  .pn-profile-content{padding:56px 20px 48px!important;}
  .pn-profile-name{font-size:26px!important;}
}
.pn-arrow:hover{gap:10px;}
.pn-cat:hover{border-color:#2C7A5B;box-shadow:0 12px 28px -16px rgba(16,32,58,0.3);transform:translateY(-2px);}
.pn-trade:hover{box-shadow:0 18px 40px -22px rgba(16,32,58,0.32);transform:translateY(-3px);}
.pn-trade-feat{box-shadow:0 10px 26px -18px rgba(169,105,27,0.55);}
.pn-trade-feat:hover{box-shadow:0 20px 44px -20px rgba(169,105,27,0.6);}
.pn-quote:hover{background:#2C7A5B;color:#fff;border-color:#2C7A5B;}
.pn-suppa:hover{background:#EAF1FF;}
.pn-suppb:hover{background:rgba(255,255,255,0.2);}
.pn-foot:hover{color:#fff;}
.pn-burger{display:none;}
.pn-burger:hover{background:#F2F0EB;border-color:#2C7A5B;color:#2C7A5B;}
@media (min-width:769px){
  .pn-mobile-menu{display:none!important;}
}
@media (max-width:1100px){
  .pn-dir-grid{grid-template-columns:repeat(3,1fr)!important;}
}
@media (max-width:900px){
  .pn-cat-grid{grid-template-columns:repeat(2,1fr)!important;}
  .pn-dir-grid{grid-template-columns:repeat(2,1fr)!important;}
  .pn-steps{grid-template-columns:1fr!important;}
  .pn-footer-grid{grid-template-columns:1fr 1fr!important;}
  .pn-hero-img{display:none!important;}
  .pn-hero{background-image:url('/images/partner-network/hero-mobile.png')!important;background-size:cover!important;background-position:center bottom!important;background-repeat:no-repeat!important;}
  .pn-h1{font-size:40px!important;}
}
@media (max-width:768px){
  .pn-nav-links{display:none!important;}
  .pn-auth{display:none!important;}
  .pn-burger{display:inline-flex!important;}
  .pn-search{flex-direction:column!important;background:transparent!important;border:none!important;box-shadow:none!important;padding:0!important;gap:10px!important;}
  .pn-field{background:#fff!important;border:1px solid #E5E0D8!important;border-radius:12px!important;min-width:0!important;flex:1 1 auto!important;}
  .pn-divider{display:none!important;}
  .pn-searchbtn{width:100%!important;padding:15px 0!important;justify-content:center!important;}
  .pn-cta-band{padding:40px 26px!important;}
}
@media (max-width:560px){
  .pn-cat-grid{grid-template-columns:1fr!important;}
  .pn-dir-grid{grid-template-columns:1fr!important;}
  .pn-footer-grid{grid-template-columns:1fr!important;}
  .pn-contact-pair{grid-template-columns:1fr!important;}
  .pn-h1{font-size:32px!important;}
  .pn-stat-div{display:none!important;}
  .pn-stats{gap:24px 28px!important;}
}
`;

// External fonts/icon CSS this page depends on. Injected into <head> at mount and
// removed at unmount. (Done imperatively rather than via react-helmet-async, which
// doesn't reliably inject <link>/<style> in this app.)
// Phosphor is vendored under public/vendor/phosphor (served same-origin) because the
// app's CSP only allows style-src/font-src from 'self' + Google Fonts — not unpkg.
export const HEAD_STYLESHEETS = [
  "https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800&family=Public+Sans:wght@400;500;600;700&display=swap",
  // Source Serif 4 + Figtree carry the supplier-profile direction (mockup 1c). Loaded
  // alongside rather than instead of the pair above, because the browse and results
  // pages still use them.
  "https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=Figtree:wght@400;500;600;700&display=swap",
  "/vendor/phosphor/regular/style.css",
  "/vendor/phosphor/bold/style.css",
  "/vendor/phosphor/fill/style.css",
];

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

export function TradeCard({
  listing,
  style,
  featured = false,
}: {
  listing: MarketplaceListing;
  style?: CSSProperties;
  /**
   * Renders the card as a PAID placement: bronze border, and the corner badge says FEATURED
   * instead of the network-membership one.
   *
   * <p>It replaces that badge rather than sitting beside it. Two pills in one corner is noise, and
   * "DAconnect Partner" is true of every card on the page — it is the promoted status that is worth
   * the space here.</p>
   *
   * <p>Default false, so the results page and every existing caller are untouched.</p>
   */
  featured?: boolean;
}) {
  // A truthy id is not a promise the picture will load. Both of these name a file that may be
  // missing from storage — a logoFileName that outlived its file is exactly what put a broken-image
  // glyph on this card — and the routes behind them answer 404 rather than substituting anything.
  // On error we fall back to precisely what an absent picture renders, so a failing URL and no URL
  // are the same card.
  const [heroFailed, setHeroFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const heroUrl =
    listing.heroImageId && !heroFailed ? listingImageUrl(listing.id, listing.heroImageId) : null;
  const logoUrl = listing.logoFileName && !logoFailed ? getPublicBusinessLogoUrl(listing.id) : null;

  // The panel's own colour, which is all there is behind a hero that is loading, absent or failed.
  // BusinessController creates businesses with BackgroundColor = "" by default, so an empty colour
  // falls back to the brand green rather than to nothing.
  const panel = listing.backgroundColor || BRAND;

  return (
    <div
      className={featured ? "pn-trade pn-trade-feat" : "pn-trade"}
      data-testid={featured ? "featured-card" : undefined}
      style={{
        background: "#fff",
        // 2px so the accent survives against the tinted band behind it; a 1px bronze line on a
        // warm background is invisible at arm's length.
        border: featured ? `2px solid ${FEATURED}` : "1px solid #E9E5DE",
        borderRadius: 16,
        overflow: "hidden",
        transition: "all .15s",
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {/* Cover panel — the hero the business uploaded to its own listing, on its own brand colour.
          Still no stock photography: a decorative picture of someone else's work reads as a claim
          about theirs. A hero is the business's own photograph of its own work, so it makes exactly
          the claim it is entitled to make — and with none uploaded the panel stays a plain colour
          rather than borrowing one.

          Nothing renders an <img> unless there is an id to render, and a failed load takes the
          <img> back out, so this panel can never show the browser's broken-image glyph. */}
      {/* 3:1, the same shape as the profile hero, so a cover is framed alike on the card and
          on the page it opens. */}
      <div data-testid="trade-panel" style={{ aspectRatio: "3 / 1", position: "relative", background: panel }}>
        {heroUrl && (
          <img
            data-testid="trade-hero"
            src={heroUrl}
            alt=""
            loading="lazy"
            onError={() => setHeroFailed(true)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            // Solid fill on a promoted card, so the badge reads as a label on the listing rather
            // than as another translucent chip over somebody's photograph.
            background: featured ? FEATURED : "rgba(255,255,255,0.95)",
            color: featured ? "#fff" : BRAND,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: featured ? "0.06em" : undefined,
            padding: "5px 10px",
            borderRadius: 999,
          }}
        >
          {featured ? (
            <>
              <i className="ph-fill ph-star" style={{ fontSize: 14 }} /> FEATURED
            </>
          ) : (
            <>
              <i className="ph-fill ph-seal-check" style={{ fontSize: 14 }} /> DAconnect Partner
            </>
          )}
        </div>

        {/* The mark, as an avatar sits on a cover photo — the same rounded square the profile page
            gives it. It has a solid fill of its own so a logo saved with a transparent background
            stays legible over a photograph, and it carries the initials when there is no logo,
            which is what keeps a card with neither picture looking deliberate rather than empty.

            Bottom-left, clear of the partner chip in the opposite corner whatever either grows to.
            The content below reserves the overhang in its padding. */}
        <div
          data-testid="trade-logo"
          style={{
            position: "absolute",
            left: 16,
            bottom: -22,
            width: 64,
            height: 64,
            borderRadius: 16,
            background: logoUrl ? "#fff" : BRAND,
            border: "3px solid #fff",
            boxShadow: "0 4px 14px rgba(28,33,28,.14)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={listing.name}
              loading="lazy"
              onError={() => setLogoFailed(true)}
              style={{ maxWidth: "76%", maxHeight: "76%", objectFit: "contain", display: "block" }}
            />
          ) : (
            <span
              style={{
                fontFamily: "'Archivo',sans-serif",
                fontWeight: 800,
                fontSize: 22,
                color: "rgba(255,255,255,0.95)",
              }}
            >
              {initials(listing.name)}
            </span>
          )}
        </div>
      </div>
      {/* Top padding clears the logo tile's overhang (22px plus its 3px border) rather than the
          tile clearing the text: the heading must not move when a business has no logo. */}
      <div style={{ padding: "33px 20px 20px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 18, margin: "0 0 5px", color: "#243139" }}>
          {listing.name}
        </h3>
        <div style={{ fontSize: 14, color: "#6A7484", fontWeight: 500, marginBottom: 14 }}>
          {listing.industries.join(" · ")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#7A8494", marginBottom: 16 }}>
          <i className="ph ph-map-pin" style={{ fontSize: 15 }} />
          {[listing.suburb, listing.state].filter(Boolean).join(", ")}
          {listing.distanceKm !== null && <> · {listing.distanceKm.toFixed(1)} km</>}
        </div>
        <div style={{ marginTop: "auto", paddingTop: 14, borderTop: "1px solid #EFECE6" }}>
          <Link
            to={`/business/${listing.id}`}
            className="pn-quote"
            style={{
              display: "block",
              textAlign: "center",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: 14,
              color: BRAND,
              padding: "11px 16px",
              borderRadius: 9,
              border: "1px solid #CDE8DB",
              background: "#EEF7F2",
            }}
          >
            View profile
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * The three promises in the closing strip. Each is one the rest of the site already makes — the
 * About and Pricing pages say "no pay-per-lead, no commission", and the listings here are the
 * businesses' own — so the strip repeats claims rather than inventing them.
 */
const FIND_TRUST = [
  { title: "Updated daily", body: "DA data pulled from council portals every day." },
  { title: "Every business is a member", body: "No scraped listings — each profile is maintained by its owner." },
  { title: "No lead fees", body: "Contact businesses directly. We never take a cut." },
];

/** The weekly cap on paid placements — `FeaturedWeek.MaxPerWeek` on the server. */
const FEATURED_PER_WEEK = 10;

/** How many businesses the list shows before "Show more". */
const PAGE_SIZE = 12;

/** The tints an initials tile cycles through, from the design. Decorative only. */
const TINTS = ["#e2f3e7", "#fdf3dc", "#e6eef7", "#f3e8ee", "#eef0e2"];

type SortKey = "featured" | "name" | "councils";

const SORTS: { key: SortKey; label: string }[] = [
  // The API's own order, which puts paid placements first — labelled for what it is.
  { key: "featured", label: "Featured first" },
  { key: "name", label: "Name (A–Z)" },
  { key: "councils", label: "Most councils" },
];

/** The design's warm "promoted" label colours — paid placement must read as different at a glance. */
const PROMO_INK = "#8a5a0b";
const PROMO_TINT = "#fdf3dc";

const FIND_CSS = `
.fb-feat{transition:box-shadow .15s,transform .15s}
.fb-feat:hover{box-shadow:0 14px 34px rgba(20,83,45,.12);transform:translateY(-2px)}
.fb-row{transition:border-color .15s}
.fb-row:hover{border-color:${C.mint}}
.fb-cat{transition:border-color .15s}
.fb-cat:hover{border-color:${C.mint}}
.fb-pill{transition:opacity .15s}
.fb-pill:hover{opacity:.88}
.fb-search input::placeholder{color:#9a9f93}
/* The design's search is one long pill. Below this width two fields and a button side by side are
   too narrow to type in, so the bar becomes a stacked card. */
@media (max-width:720px){
  .fb-search{flex-direction:column;align-items:stretch!important;border-radius:20px!important}
  .fb-search-div{display:none}
  .fb-search-go{text-align:center}
}
`;

/**
 * One business in the list: its mark, what it does, where, and how widely it works.
 *
 * Its own component because the logo needs its own failure state — a logo file that outlived its
 * upload answers 404, and that must fall back to the initials rather than a broken-image glyph,
 * exactly as `TradeCard` does.
 */
function BusinessRow({ listing, tint }: { listing: MarketplaceListing; tint: string }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = listing.logoFileName && !logoFailed ? getPublicBusinessLogoUrl(listing.id) : null;
  const councils = listing.councilAreas?.length ?? 0;
  const where = [listing.suburb, listing.state].filter(Boolean).join(", ");

  return (
    <Link
      to={`/business/${listing.id}`}
      data-testid="business-row"
      className="fb-row"
      style={{
        background: "#fff",
        border: `1px solid ${C.cardLine}`,
        borderRadius: 14,
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        color: "inherit",
      }}
    >
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: logoUrl ? "#fff" : tint,
          border: logoUrl ? `1px solid ${C.cardLine}` : "none",
          color: C.deep,
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          fontSize: 15,
          flex: "none",
          overflow: "hidden",
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            loading="lazy"
            onError={() => setLogoFailed(true)}
            style={{ maxWidth: "80%", maxHeight: "80%", objectFit: "contain", display: "block" }}
          />
        ) : (
          initials(listing.name)
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink }}>{listing.name}</div>
        <div style={{ fontSize: 12.5, color: C.bodyMuted, marginTop: 2 }}>
          {listing.industries.length > 0 && (
            <b style={{ color: C.link, fontWeight: 600 }}>{listing.industries.join(" · ")}</b>
          )}
          {listing.industries.length > 0 && where && " · "}
          {where}
        </div>
        {/* Only a number the listing actually carries. "Member since" is in the design, but the
            browse endpoint does not send it — computing it needs the per-business lookup — so it
            is left out rather than guessed. */}
        {councils > 0 && (
          <div style={{ fontSize: 12, color: C.footInk, marginTop: 2 }}>
            {councils} {councils === 1 ? "council" : "councils"} serviced
          </div>
        )}
      </div>
      <span
        style={{
          border: "1px solid #cfe6d6",
          color: C.deep,
          borderRadius: 99,
          fontSize: 12,
          fontWeight: 700,
          padding: "7px 14px",
          flex: "none",
        }}
      >
        Enquire
      </span>
    </Link>
  );
}

/**
 * A paid placement, as the design draws it: the business's own picture on top, labelled FEATURED.
 *
 * The picture is the hero from the business's published listing — never stock photography, which
 * would be a claim about somebody else's work — and with none it is the business's own brand colour.
 */
function FeaturedCard({ listing }: { listing: MarketplaceListing }) {
  const [heroFailed, setHeroFailed] = useState(false);
  const heroUrl =
    listing.heroImageId && !heroFailed ? listingImageUrl(listing.id, listing.heroImageId) : null;
  const councils = listing.councilAreas?.length ?? 0;
  const where = [listing.suburb, listing.state].filter(Boolean).join(", ");

  return (
    <Link
      to={`/business/${listing.id}`}
      data-testid="featured-card"
      className="fb-feat"
      style={{
        background: "#fff",
        border: `1px solid ${C.cardLine}`,
        borderRadius: 16,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        color: "inherit",
      }}
    >
      <div style={{ aspectRatio: "3 / 1", position: "relative", background: listing.backgroundColor || C.bandLine }}>
        {heroUrl && (
          <img
            src={heroUrl}
            alt=""
            loading="lazy"
            onError={() => setHeroFailed(true)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        <span
          style={{
            position: "absolute",
            left: 10,
            top: 10,
            background: "rgba(255,255,255,.94)",
            color: PROMO_INK,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".06em",
            padding: "3px 8px",
            borderRadius: 6,
          }}
        >
          ★ FEATURED
        </span>
      </div>
      <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{listing.name}</div>
        {listing.industries.length > 0 && (
          <div style={{ fontSize: 12.5, color: C.link, fontWeight: 600 }}>{listing.industries.join(" · ")}</div>
        )}
        <div style={{ fontSize: 12.5, color: C.bodyMuted }}>
          {where}
          {councils > 0 && `${where ? " · " : ""}${councils} ${councils === 1 ? "council" : "councils"}`}
        </div>
        <span
          style={{
            marginTop: "auto",
            paddingTop: 10,
            alignSelf: "flex-start",
          }}
        >
          <span
            style={{
              display: "inline-block",
              background: C.tint,
              color: C.deep,
              borderRadius: 99,
              fontSize: 12,
              fontWeight: 700,
              padding: "7px 14px",
            }}
          >
            View listing
          </span>
        </span>
      </div>
    </Link>
  );
}

/** A section's heading row: optional eyebrow, serif title, and whatever sits on the right. */
function SectionHead({
  eyebrow, title, sub, right,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: string;
  right?: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 16,
        flexWrap: "wrap",
      }}
    >
      <div>
        {eyebrow}
        <h2 style={{ fontFamily: SERIF, fontSize: 28, fontWeight: 500, margin: eyebrow ? "8px 0 0" : 0, color: C.ink }}>
          {title}
        </h2>
        {sub && <div style={{ fontSize: 13.5, color: C.bodyMuted, marginTop: 4 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

/**
 * DAconnect's public "Find a business" page, served at `/` — the Claude Design "Find a Business"
 * layout on live marketplace data.
 *
 * Standalone and public (no app chrome). Everything on it is real: the categories and their counts
 * come from the categories endpoint, which already omits empty ones; the businesses and the paid
 * placements come from ONE listings request. Nothing is invented to fill a gap — an outage or an
 * empty marketplace shows an empty section, never filler, and no count renders as "0" while its
 * request is still in flight.
 */
export default function PartnerNetwork() {
  const [whatValue, setWhatValue] = useState("");
  const [whereValue, setWhereValue] = useState("");
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [partners, setPartners] = useState<MarketplaceListing[]>([]);
  // "Not loaded yet" and "loaded, genuinely zero businesses" must never render the same way.
  const [partnersLoaded, setPartnersLoaded] = useState(false);
  /** Ids holding a live paid placement, from the same request that loaded the businesses. */
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);
  /** The category tile picked, which filters the list below it. Null is "all". */
  const [category, setCategory] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("featured");
  const [shown, setShown] = useState(PAGE_SIZE);
  const navigate = useNavigate();
  // "List free" only while the live Marketplace plan really is $0 — read from the plans the
  // Pricing page reads, so the two pages cannot quote different prices. Unknown reads as not free.
  const plans = usePlans();
  const listingIsFree = subscriptionTotalMinor(plans, "marketplace", 1) === 0;

  useEffect(() => {
    let cancelled = false;
    Promise.all([getMarketplaceCategories(), getMarketplaceListings()])
      .then(([cats, listings]) => {
        if (cancelled) return;
        setCategories(cats);
        setPartners(listings.items);
        // Guarded, not merely typed: an older server build omits the field, and `undefined` must
        // read as "nothing is featured" rather than throwing on the page every visitor lands on.
        setFeaturedIds(Array.isArray(listings.featuredIds) ? listings.featuredIds : []);
        setPartnersLoaded(true);
      })
      .catch(() => {
        // An outage shows empty sections, never invented filler. partnersLoaded stays false — a
        // failed fetch is not "loaded, genuinely zero businesses".
        if (!cancelled) {
          setCategories([]);
          setPartners([]);
          setFeaturedIds([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useSiteStyles(FIND_CSS);

  // The paid placements, in the order the API already put them (featured first). Sliced to the
  // weekly cap as a DOM bound, not a correctness rule — the server enforces ten a week.
  const featuredPartners = featuredIds.length === 0
    ? []
    : partners.filter((p) => featuredIds.includes(p.id)).slice(0, FEATURED_PER_WEEK);

  // Counted from EVERY placement the server says is live, not just the ones drawn: a placement held
  // by a business whose listing has since gone unpublished still uses up one of the ten, and a strip
  // that counted only the cards on screen would offer a spot the server will refuse to sell.
  const spotsLeft = Math.max(0, FEATURED_PER_WEEK - featuredIds.length);

  const picked = categories.find((c) => c.slug === category) ?? null;
  const filtered = picked ? partners.filter((p) => p.categorySlugs?.includes(picked.slug)) : partners;
  const sorted = sort === "featured"
    ? filtered
    : [...filtered].sort((a, b) =>
      sort === "name"
        ? a.name.localeCompare(b.name)
        : (b.councilAreas?.length ?? 0) - (a.councilAreas?.length ?? 0) || a.name.localeCompare(b.name));
  const visible = sorted.slice(0, shown);

  const runSearch = (e?: FormEvent) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (whatValue.trim()) params.set("what", whatValue.trim());
    if (whereValue.trim()) params.set("where", whereValue.trim());
    navigate(`/results?${params.toString()}`);
  };

  const pickCategory = (slug: string) => {
    setCategory((current) => (current === slug ? null : slug));
    setShown(PAGE_SIZE);
  };

  const wrap = SITE_WRAP;
  const fieldLabel: CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", color: C.bodyMuted };
  const fieldInput: CSSProperties = {
    border: "none",
    outline: "none",
    fontFamily: "inherit",
    fontSize: 14.5,
    color: C.ink,
    width: "100%",
    padding: 0,
    background: "transparent",
  };

  return (
    <div className="site-root" style={SITE_ROOT}>
      <PageMeta
        title="Find a business | DAconnect"
        description="Search the trades and suppliers that use DAconnect — free for homeowners and developers, no sign-up needed."
      />

      <SiteHeader active="find" />

      {/* SEARCH BAND */}
      <section
        style={{
          background: "linear-gradient(180deg,#e2f3e7,#f2faf4)",
          borderBottom: "1px solid #d3ecdb",
        }}
      >
        <div style={{ ...wrap, padding: "52px 28px 44px", display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 18, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", color: C.link }}>DACONNECT MARKETPLACE</div>
              <h1
                style={{
                  fontFamily: SERIF,
                  fontSize: "clamp(30px,3.6vw,42px)",
                  fontWeight: 500,
                  lineHeight: 1.15,
                  margin: "6px 0 0",
                }}
              >
                Find a <b style={{ color: C.deep, fontWeight: 600 }}>trade or supplier</b> near you.
              </h1>
              <p style={{ fontSize: 14.5, color: C.inkMuted, margin: "6px 0 0" }}>
                Search the businesses that use DAconnect — free for homeowners and developers, no sign-up needed.
              </p>
            </div>
            <span style={{ fontSize: 13, color: C.inkMuted }}>
              Are you a business?{" "}
              <Link to="/signup" style={{ color: C.deep, fontWeight: 700 }}>
                {listingIsFree ? "List free →" : "List your business →"}
              </Link>
            </span>
          </div>

          <form
            role="search"
            onSubmit={runSearch}
            className="fb-search"
            style={{
              display: "flex",
              background: "#fff",
              border: `1px solid ${C.cardLine}`,
              borderRadius: 99,
              boxShadow: "0 10px 30px rgba(20,83,45,.08)",
              padding: 7,
              alignItems: "center",
              gap: 4,
            }}
          >
            <label style={{ flex: 1.3, padding: "5px 18px", minWidth: 0, display: "flex", alignItems: "center", gap: 12, cursor: "text" }}>
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.link} strokeWidth="2.2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-4-4" />
              </svg>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ ...fieldLabel, display: "block" }}>WHAT DO YOU NEED?</span>
                <input
                  value={whatValue}
                  onChange={(e) => setWhatValue(e.target.value)}
                  placeholder="Builder, blinds, solar…"
                  style={fieldInput}
                />
              </span>
            </label>
            <span className="fb-search-div" aria-hidden="true" style={{ width: 1, height: 36, background: C.line, flex: "none" }} />
            <label style={{ flex: 1, padding: "5px 18px", minWidth: 0, display: "flex", alignItems: "center", gap: 12, cursor: "text" }}>
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.link} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12z" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ ...fieldLabel, display: "block" }}>WHERE?</span>
                <input
                  value={whereValue}
                  onChange={(e) => setWhereValue(e.target.value)}
                  placeholder="Suburb or postcode"
                  style={fieldInput}
                />
              </span>
            </label>
            <button
              type="submit"
              className="fb-pill fb-search-go"
              style={{
                background: C.deep,
                color: "#fff",
                border: "none",
                borderRadius: 99,
                fontSize: 14,
                fontWeight: 700,
                padding: "14px 30px",
                cursor: "pointer",
                flex: "none",
                fontFamily: "inherit",
              }}
            >
              Search
            </button>
          </form>
        </div>
      </section>

      {/* FEATURED — only when somebody has actually bought a placement. An empty "Featured this
          week" heading would read as a marketplace nobody is in. */}
      {featuredPartners.length > 0 && (
        <section data-testid="featured-strip" style={{ ...wrap, padding: "36px 28px 0" }}>
          <SectionHead
            eyebrow={(
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span
                  style={{
                    background: PROMO_TINT,
                    color: PROMO_INK,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: ".06em",
                    padding: "4px 9px",
                    borderRadius: 6,
                  }}
                >
                  PROMOTED
                </span>
                <span style={{ fontSize: 12.5, color: C.bodyMuted }}>
                  Paid placement · max {FEATURED_PER_WEEK} businesses per week
                </span>
              </div>
            )}
            title="Featured this week"
            right={(
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: C.bodyMuted }}>
                  <b style={{ color: C.deep }}>{spotsLeft} of {FEATURED_PER_WEEK}</b> spots left
                </span>
                <Link
                  to="/pricing"
                  className="fb-pill"
                  style={{
                    border: `1.5px solid ${C.deep}`,
                    color: C.deep,
                    borderRadius: 99,
                    fontSize: 13,
                    fontWeight: 700,
                    padding: "9px 18px",
                  }}
                >
                  Get featured
                </Link>
              </div>
            )}
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 16 }}>
            {featuredPartners.map((listing) => (
              <FeaturedCard key={listing.id} listing={listing} />
            ))}
          </div>
        </section>
      )}

      {/* CATEGORIES — from the live taxonomy, which already omits anything with nobody in it. */}
      {categories.length > 0 && (
        <section style={{ ...wrap, padding: "40px 28px 0" }}>
          <SectionHead
            eyebrow={<div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", color: C.link }}>MARKETPLACE</div>}
            title="Browse by category"
            right={(
              <Link to="/results" style={{ fontSize: 13.5, fontWeight: 700, color: C.link }}>
                All businesses →
              </Link>
            )}
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
            {categories.map((c) => {
              const on = c.slug === category;
              return (
                <button
                  key={c.slug}
                  type="button"
                  aria-pressed={on}
                  onClick={() => pickCategory(c.slug)}
                  className="fb-cat"
                  style={{
                    background: on ? C.deep : "#fff",
                    border: `1px solid ${on ? C.deep : C.cardLine}`,
                    borderRadius: 14,
                    padding: "14px 16px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    fontFamily: "inherit",
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: on ? "#fff" : C.ink }}>{c.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: on ? C.mint : C.footInk }}>{c.count}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* THE LIST */}
      <section style={{ ...wrap, padding: "40px 28px 56px" }}>
        <SectionHead
          title={picked
            ? `${picked.label} · ${filtered.length} ${filtered.length === 1 ? "business" : "businesses"}`
            : "All businesses"}
          sub="Every business here is a DAconnect member servicing the councils shown."
          right={(
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <label htmlFor="fb-sort" style={{ fontSize: 13, color: C.bodyMuted }}>Sort</label>
              <select
                id="fb-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                style={{
                  border: `1px solid ${C.cardLine}`,
                  background: "#fff",
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#3d423c",
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              {picked && (
                <button
                  type="button"
                  onClick={() => { setCategory(null); setShown(PAGE_SIZE); }}
                  style={{ background: "none", border: "none", padding: 0, fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: "#b91c1c", cursor: "pointer" }}
                >
                  Clear filter ✕
                </button>
              )}
            </div>
          )}
        />

        {partnersLoaded && sorted.length === 0 && (
          <p style={{ fontSize: 14, color: C.bodyMuted }}>
            {picked ? "Nobody is listed in this category yet." : "No businesses are listed yet."}
          </p>
        )}

        <div
          data-testid="business-list"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(360px,100%),1fr))", gap: 12 }}
        >
          {visible.map((listing, i) => (
            <BusinessRow key={listing.id} listing={listing} tint={TINTS[i % TINTS.length]} />
          ))}
        </div>

        {sorted.length > shown && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <button
              type="button"
              onClick={() => setShown((n) => n + PAGE_SIZE)}
              className="fb-pill"
              style={{
                background: "#fff",
                border: `1px solid ${C.cardLine}`,
                borderRadius: 99,
                fontFamily: "inherit",
                fontSize: 13.5,
                fontWeight: 700,
                color: C.ink,
                padding: "11px 26px",
                cursor: "pointer",
              }}
            >
              Show more businesses
            </button>
          </div>
        )}
      </section>

      <SiteFooter trust={FIND_TRUST} />
    </div>
  );
}
